import { ReactElement, useMemo } from 'react'
import { GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import Link from 'next/link'
import retriveMessages, { LocaleType } from '../../messages/message'
import PageLayout from '../../components/page-layout/page-layout'
import { NextPageWithLayout } from '../../common/common-types'
import { CommonData, getPageCommonData } from '../../service/common.service'
import { findRegionByLocale } from '../../common/region-locale'
import { withSEO } from '../../common/with-seo'

interface SitemapPageProps {
  data: CommonData
}

interface SitemapSection {
  title: string
  links: Array<{
    name: string
    url: string
  }>
}

const Sitemap: NextPageWithLayout<SitemapPageProps> = ({ data }) => {
  const t = useTranslations()
  const tNavbar = useTranslations('Navbar')
  const router = useRouter()
  const { locale } = router

  // 构建站点地图数据
  const columns = useMemo<SitemapSection[][]>(() => {
    const col1: SitemapSection[] = []
    const col2: SitemapSection[] = []
    const col3: SitemapSection[] = []

    // 第一列：首页、产品
    // 首页
    col1.push({
      title: t('SitemapHome') || 'Home',
      links: [
        {
          name: t('SitemapHome') || 'Home',
          url: '/',
        },
      ],
    })

    // 产品页面
    if (data?.topNavProducts && data.topNavProducts.length > 0) {
      const productLinks: Array<{ name: string; url: string }> = []
      data.topNavProducts?.[0]?.item?.forEach((productGroup) => {
        productGroup.item.forEach((product) => {
          if (product.jumpUrl) {
            productLinks.push({
              name: product.title,
              url: product.jumpUrl,
            })
          }
        })
      })

      if (productLinks.length > 0) {
        col1.push({
          title: tNavbar('PuduProducts') || 'Products',
          links: productLinks,
        })
      }
    }

    // 第二列：解决方案、客户案例、资源
    // 解决方案页面
    if (data?.solutionNav && data.solutionNav.length > 0) {
      col2.push({
        title: tNavbar('Solutions') || 'Solutions',
        links: data.solutionNav.map((solution) => ({
          name: solution.title,
          url: solution.jumpUrl,
        })),
      })
    }

    // 客户案例
    col2.push({
      title: tNavbar('CustomerCases') || 'Customer Cases',
      links: [
        {
          name: tNavbar('CustomerCases') || 'Customer Cases',
          url: '/case-studies',
        },
      ],
    })

    // 资源页面
    const resourceLinks: Array<{ name: string; url: string }> = [
      {
        name: tNavbar('ResourceNews') || 'News',
        url: '/news',
      },
      {
        name: tNavbar('ResourceEvents') || 'Events',
        url: '/events',
      },
      {
        name: tNavbar('ResourceDownload') || 'Download',
        url: '/download',
      },
    ]

    col2.push({
      title: tNavbar('Resources') || 'Resources',
      links: resourceLinks,
    })

    // 第三列：关于我们、服务与支持、其他页面
    // 关于我们
    col3.push({
      title: tNavbar('AboutUs') || 'About Us',
      links: [
        {
          name: tNavbar('AboutUs') || 'About Us',
          url: '/company',
        },
      ],
    })

    // 服务与支持
    col3.push({
      title: tNavbar('ServiceAndSupport') || 'Service & Support',
      links: [
        {
          name: tNavbar('ServiceAndSupport') || 'Service & Support',
          url: '/support',
        },
        {
          name: tNavbar('ContactUs') || 'Contact Us',
          url: '/contact-us',
        },
      ],
    })

    // 其他页面
    col3.push({
      title: t('SitemapOtherPages') || 'Other Pages',
      links: [
        {
          name: t('SitemapOpenPlatform') || 'Open Platform',
          url: '/open-platform',
        },
        {
          name: t('Privacy') || 'Privacy Policy',
          url: '/privacy',
        },
      ],
    })

    return [col1, col2, col3]
  }, [data, tNavbar, t])

  return (
    <div className="w-full bg-white min-h-screen py-12 laptop:py-16">
      <div className="mx-auto w-full max-w-300 px-6 laptop:px-0">
        {/* 页面标题 */}
        <div className="mb-8 laptop:mb-12">
          <h1 className="text-3xl laptop:text-4xl font-semibold text-black mb-2">{t('Sitemap')}</h1>
        </div>

        {/* 三列布局 */}
        <div className="grid grid-cols-1 laptop:grid-cols-3 gap-8 laptop:gap-12">
          {columns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex flex-col gap-8">
              {column.map((section, sectionIndex) => (
                <div key={`${columnIndex}-${sectionIndex}`} className="flex flex-col gap-4">
                  <h3 className="text-base laptop:text-lg font-semibold text-black mb-2">{section.title}</h3>
                  <ul className="flex flex-col gap-2 list-none pl-0">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex} className="flex items-start">
                        <Link href={link.url}>
                          <a className="text-black/70 hover:text-pdblue hover:underline flex items-center gap-2 text-sm laptop:text-base leading-relaxed">
                            <span className="text-black/50 text-base laptop:text-lg">›</span>
                            <span>{link.name}</span>
                          </a>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

Sitemap.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export const getServerSideProps = withSEO(
  async ({ locale }: GetServerSidePropsContext) => {
    const messages = retriveMessages(['shared', '_app'], locale as LocaleType)
    const region = findRegionByLocale(locale as string)

    let data: CommonData | null = null
    try {
      data = await getPageCommonData(locale as string, true)
    } catch (error) {
      console.error('Failed to fetch common data:', error)
    }

    return {
      props: {
        messages,
        data: data || {
          topNavProducts: [],
          bottomNav: [],
          eventNav: [],
          threeJump: [],
          friendLink: [],
          solutionNav: [],
          productBannerMediaUrl: '',
          ProductBannerMediaUrlH5: '',
        },
      },
    }
  },
  () => '/sitemap'
)

export default Sitemap
